import { useState, useMemo } from 'react';
import { useInventory, DEPARTMENTS } from '@/contexts/InventoryContext';
import type { InventoryItem, StockTransaction } from '@/mocks/data';
import { useApp } from '@/contexts/AppContext';

const CATEGORIES = ['Housekeeping', 'Kitchen', 'Electronics', 'Furniture', 'Linen', 'Toiletries', 'Maintenance', 'Office'];

type TabType = 'main-store' | 'departments' | 'low-stock' | 'ledger';

export default function InventoryStock() {
  const {
    items, transactions, vendors, deptStocks,
    addItem, deleteItem,
    stockIn, stockOut, stockAdjust, transferToDept,
    setDeptParLevel,
    lowStockItems, getDeptStock, getItemTransactions,
    getStockLedger,
  } = useInventory();
  const { addToast } = useApp();

  const [search, setSearch] = useState('');
  const [filterCategory, setFilterCategory] = useState('all');
  const [activeTab, setActiveTab] = useState<TabType>('main-store');
  const [activeDept, setActiveDept] = useState(DEPARTMENTS[0]);
  const [showForm, setShowForm] = useState(false);
  const [showTxModal, setShowTxModal] = useState(false);
  const [txMode, setTxMode] = useState<'stock-in' | 'stock-out' | 'adjust' | 'transfer'>('stock-in');
  const [selectedItem, setSelectedItem] = useState<InventoryItem | null>(null);
  const [txForm, setTxForm] = useState({ qty: '', vendor_id: '', dept: '', reason: '', bill_no: '', price: '' });

  const [formData, setFormData] = useState({ name: '', category: 'Housekeeping', unit: '', min_stock_level: '', current_qty: '' });

  const [ledgerItemId, setLedgerItemId] = useState('');

  const filtered = useMemo(() => {
    return items.filter((i) => {
      const q = search.toLowerCase();
      if (search && !i.name.toLowerCase().includes(q)) return false;
      if (filterCategory !== 'all' && i.category !== filterCategory) return false;
      return true;
    });
  }, [items, search, filterCategory]);

  const totalValue = items.reduce((s, i) => s + i.current_qty * 50, 0);

  const handleAddItem = () => {
    if (!formData.name || !formData.unit) { addToast('error', 'Name and unit are required'); return; }
    const newItem: InventoryItem = {
      id: `inv-${Date.now()}`,
      name: formData.name,
      category: formData.category,
      unit: formData.unit,
      min_stock_level: Number(formData.min_stock_level) || 0,
      current_qty: Number(formData.current_qty) || 0,
      created_at: new Date().toISOString(),
    };
    addItem(newItem);
    addToast('success', `"${formData.name}" added to inventory`);
    setShowForm(false);
    setFormData({ name: '', category: 'Housekeeping', unit: '', min_stock_level: '', current_qty: '' });
  };

  const handleDelete = (id: string) => {
    const item = items.find((i) => i.id === id);
    if (confirm(`Delete "${item?.name}" from inventory?`)) {
      deleteItem(id);
      addToast('success', 'Item deleted');
    }
  };

  const openTx = (item: InventoryItem, mode: 'stock-in' | 'stock-out' | 'adjust' | 'transfer') => {
    setSelectedItem(item);
    setTxMode(mode);
    setTxForm({ qty: '', vendor_id: '', dept: DEPARTMENTS[0], reason: '', bill_no: '', price: '' });
    setShowTxModal(true);
  };

  const handleTxSave = () => {
    if (!selectedItem || !txForm.qty) { addToast('error', 'Quantity is required'); return; }
    const qty = Number(txForm.qty);
    if (qty <= 0) { addToast('error', 'Quantity must be positive'); return; }

    const itemName = selectedItem.name;

    switch (txMode) {
      case 'stock-in':
        if (!txForm.vendor_id) { addToast('error', 'Vendor is required for stock entry'); return; }
        stockIn(selectedItem.id, qty, txForm.vendor_id, txForm.bill_no, Number(txForm.price) || 0);
        addToast('success', `${qty} ${selectedItem.unit} of "${itemName}" added — stock updated`);
        break;
      case 'stock-out':
        if (!txForm.dept) { addToast('error', 'Department is required'); return; }
        if (qty > selectedItem.current_qty) { addToast('error', `Only ${selectedItem.current_qty} available in stock`); return; }
        stockOut(selectedItem.id, qty, txForm.dept, txForm.reason);
        addToast('success', `${qty} ${selectedItem.unit} of "${itemName}" used by ${txForm.dept}`);
        break;
      case 'adjust':
        stockAdjust(selectedItem.id, Number(txForm.qty), txForm.reason);
        addToast('success', `"${itemName}" adjusted by ${txForm.qty}`);
        break;
      case 'transfer':
        if (!txForm.dept) { addToast('error', 'Department is required'); return; }
        if (qty > selectedItem.current_qty) { addToast('error', `Only ${selectedItem.current_qty} available in main store`); return; }
        transferToDept(selectedItem.id, qty, txForm.dept);
        addToast('success', `${qty} ${selectedItem.unit} of "${itemName}" transferred to ${txForm.dept}`);
        break;
    }
    setShowTxModal(false);
  };

  const getStockStatus = (item: InventoryItem) => {
    if (item.current_qty === 0) return { label: 'Out of Stock', color: 'bg-red-100 text-red-700', icon: 'ri-close-circle-line' };
    if (item.current_qty < item.min_stock_level) return { label: 'Low Stock', color: 'bg-amber-100 text-amber-700', icon: 'ri-alert-line' };
    if (item.current_qty <= item.min_stock_level * 1.5) return { label: 'Adequate', color: 'bg-yellow-100 text-yellow-700', icon: 'ri-check-line' };
    return { label: 'Sufficient', color: 'bg-green-100 text-green-700', icon: 'ri-check-double-line' };
  };

  // Tab: Departments
  const deptItems = useMemo(() => {
    return items.filter((item) => {
      const ds = deptStocks.find((d) => d.dept === activeDept && d.itemId === item.id);
      return ds && ds.parLevel > 0;
    });
  }, [items, deptStocks, activeDept]);

  const deptLowCount = deptItems.filter((item) => {
    const ds = deptStocks.find((d) => d.dept === activeDept && d.itemId === item.id);
    return ds && ds.qty < ds.parLevel;
  }).length;

  const ledgerEntries = useMemo(() => {
    if (!ledgerItemId) return [];
    return getStockLedger(ledgerItemId);
  }, [ledgerItemId, getStockLedger]);

  const handleParChange = (itemId: string, value: number) => {
    setDeptParLevel(activeDept, itemId, value);
  };

  return (
    <div>
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-semibold text-navy-800">Inventory Control</h1>
          <p className="text-navy-500 mt-1">Main store, department stocks, and par level management.</p>
        </div>
        <button onClick={() => setShowForm(true)} className="flex items-center gap-2 px-4 py-2.5 bg-navy-700 text-white rounded-lg hover:bg-navy-800 transition-colors cursor-pointer whitespace-nowrap">
          <i className="ri-add-line" /> Add Item
        </button>
      </div>

      {/* Stats Row */}
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-4 mb-6">
        <div className="bg-white rounded-xl border border-navy-100 p-4">
          <div className="text-xs text-navy-500 mb-1">Total Items</div>
          <div className="text-2xl font-bold text-navy-800">{items.length}</div>
        </div>
        <div className="bg-white rounded-xl border border-navy-100 p-4">
          <div className="text-xs text-navy-500 mb-1">Low Stock (Main)</div>
          <div className={`text-2xl font-bold ${lowStockItems.length > 0 ? 'text-red-600' : 'text-green-600'}`}>{lowStockItems.length}</div>
        </div>
        <div className="bg-white rounded-xl border border-navy-100 p-4">
          <div className="text-xs text-navy-500 mb-1">Categories</div>
          <div className="text-2xl font-bold text-navy-800">{new Set(items.map((i) => i.category)).size}</div>
        </div>
        <div className="bg-white rounded-xl border border-navy-100 p-4">
          <div className="text-xs text-navy-500 mb-1">Est. Value</div>
          <div className="text-2xl font-bold text-navy-800">Rs. {totalValue.toLocaleString('en-IN')}</div>
        </div>
        <div className="bg-white rounded-xl border border-navy-100 p-4">
          <div className="text-xs text-navy-500 mb-1">Vendors</div>
          <div className="text-2xl font-bold text-navy-800">{vendors.length}</div>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex items-center gap-1 mb-6 bg-navy-50 rounded-xl p-1 w-fit">
        {[
          { key: 'main-store', label: 'Main Store' },
          { key: 'departments', label: 'Departments' },
          { key: 'low-stock', label: `Low Stock (${lowStockItems.length})` },
          { key: 'ledger', label: 'Ledger' },
        ].map((tab) => (
          <button
            key={tab.key}
            onClick={() => setActiveTab(tab.key as TabType)}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-all cursor-pointer whitespace-nowrap ${
              activeTab === tab.key ? 'bg-white text-navy-800 shadow-sm' : 'text-navy-500 hover:text-navy-700'
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Department Tabs (when department view active) */}
      {activeTab === 'departments' && (
        <div className="flex items-center gap-1 mb-4 flex-wrap">
          {DEPARTMENTS.map((dept) => {
            const deptLow = deptStocks.filter((ds) => ds.dept === dept && ds.parLevel > 0 && ds.qty < ds.parLevel).length;
            return (
              <button
                key={dept}
                onClick={() => setActiveDept(dept)}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-all cursor-pointer whitespace-nowrap ${
                  activeDept === dept ? 'bg-navy-700 text-white' : 'bg-white border border-navy-200 text-navy-600 hover:bg-navy-50'
                }`}
              >
                {dept}
                {deptLow > 0 && <span className="ml-1.5 px-1.5 py-0.5 rounded-full text-xs bg-red-500 text-white">{deptLow}</span>}
              </button>
            );
          })}
        </div>
      )}

      {/* Filters (Main Store only) */}
      {activeTab === 'main-store' && (
        <div className="bg-white rounded-xl border border-navy-100 p-4 mb-6">
          <div className="flex flex-wrap gap-3 items-end">
            <div className="flex-1 min-w-[200px]">
              <label className="block text-xs font-medium text-navy-500 mb-1.5">Search</label>
              <div className="relative">
                <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-navy-400" />
                <input type="text" placeholder="Search items..." value={search} onChange={(e) => setSearch(e.target.value)} className="w-full pl-9 pr-3 py-2.5 border border-navy-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-navy-500" />
              </div>
            </div>
            <div>
              <label className="block text-xs font-medium text-navy-500 mb-1.5">Category</label>
              <select value={filterCategory} onChange={(e) => setFilterCategory(e.target.value)} className="px-3 py-2.5 border border-navy-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-navy-500">
                <option value="all">All</option>
                {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
          </div>
        </div>
      )}

      {/* Main Store Tab Content */}
      {activeTab === 'main-store' && (
        <div className="bg-white rounded-xl border border-navy-100 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-navy-50">
                <tr>
                  <th className="text-left px-5 py-3 font-semibold text-navy-700">Item</th>
                  <th className="text-left px-5 py-3 font-semibold text-navy-700">Category</th>
                  <th className="text-left px-5 py-3 font-semibold text-navy-700">Unit</th>
                  <th className="text-right px-5 py-3 font-semibold text-navy-700">Min Stock</th>
                  <th className="text-right px-5 py-3 font-semibold text-navy-700">Current</th>
                  <th className="text-center px-5 py-3 font-semibold text-navy-700">Status</th>
                  <th className="text-center px-5 py-3 font-semibold text-navy-700">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((item) => {
                  const status = getStockStatus(item);
                  const txHistory = getItemTransactions(item.id);
                  return (
                    <tr key={item.id} className="border-t border-navy-100 hover:bg-navy-50/50 transition-colors">
                      <td className="px-5 py-3.5">
                        <div className="font-medium text-navy-800">{item.name}</div>
                        {txHistory.length > 0 && <div className="text-xs text-navy-400">{txHistory.length} transactions</div>}
                      </td>
                      <td className="px-5 py-3.5 text-navy-600">{item.category}</td>
                      <td className="px-5 py-3.5 text-navy-500">{item.unit}</td>
                      <td className="px-5 py-3.5 text-right text-navy-600">{item.min_stock_level}</td>
                      <td className="px-5 py-3.5 text-right">
                        <span className={`font-medium ${item.current_qty < item.min_stock_level ? 'text-red-600' : 'text-navy-800'}`}>{item.current_qty}</span>
                      </td>
                      <td className="px-5 py-3.5 text-center">
                        <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium ${status.color}`}>
                          <i className={status.icon} /> {status.label}
                        </span>
                      </td>
                      <td className="px-5 py-3.5">
                        <div className="flex items-center justify-center gap-1">
                          <button onClick={() => openTx(item, 'stock-in')} className="p-1.5 text-green-600 hover:bg-green-50 rounded transition-colors cursor-pointer" title="Stock Entry — Vendor purchase"><i className="ri-arrow-down-circle-line" /></button>
                          <button onClick={() => openTx(item, 'stock-out')} className="p-1.5 text-amber-600 hover:bg-amber-50 rounded transition-colors cursor-pointer" title="Stock Usage — Dept consumption"><i className="ri-arrow-up-circle-line" /></button>
                          <button onClick={() => openTx(item, 'transfer')} className="p-1.5 text-blue-600 hover:bg-blue-50 rounded transition-colors cursor-pointer" title="Transfer to Department"><i className="ri-arrow-left-right-line" /></button>
                          <button onClick={() => openTx(item, 'adjust')} className="p-1.5 text-navy-600 hover:bg-navy-50 rounded transition-colors cursor-pointer" title="Adjust Stock"><i className="ri-equalizer-line" /></button>
                          <button onClick={() => handleDelete(item.id)} className="p-1.5 text-red-500 hover:bg-red-50 rounded transition-colors cursor-pointer" title="Delete"><i className="ri-delete-bin-line" /></button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Departments Tab Content */}
      {activeTab === 'departments' && (
        <div className="bg-white rounded-xl border border-navy-100 overflow-hidden">
          <div className="p-4 border-b border-navy-100 flex items-center justify-between">
            <div>
              <h3 className="font-semibold text-navy-800">{activeDept} Stock</h3>
              <p className="text-xs text-navy-500 mt-0.5">
                {deptItems.length} items tracked · {deptLowCount} below par level
              </p>
            </div>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-navy-50">
                <tr>
                  <th className="text-left px-5 py-3 font-semibold text-navy-700">Item</th>
                  <th className="text-center px-5 py-3 font-semibold text-navy-700">Current Stock</th>
                  <th className="text-center px-5 py-3 font-semibold text-navy-700">Par Level</th>
                  <th className="text-center px-5 py-3 font-semibold text-navy-700">Status</th>
                  <th className="text-center px-5 py-3 font-semibold text-navy-700">Actions</th>
                </tr>
              </thead>
              <tbody>
                {deptItems.map((item) => {
                  const ds = deptStocks.find((d) => d.dept === activeDept && d.itemId === item.id);
                  const par = ds?.parLevel || 0;
                  const current = ds?.qty || 0;
                  const isLow = current < par;
                  return (
                    <tr key={item.id} className="border-t border-navy-100 hover:bg-navy-50/50 transition-colors">
                      <td className="px-5 py-3.5">
                        <div className="font-medium text-navy-800">{item.name}</div>
                        <div className="text-xs text-navy-400">{item.unit}</div>
                      </td>
                      <td className="px-5 py-3.5 text-center">
                        <span className={`font-medium ${isLow ? 'text-red-600' : 'text-navy-800'}`}>{current}</span>
                      </td>
                      <td className="px-5 py-3.5 text-center">
                        <input
                          type="number"
                          value={par}
                          onChange={(e) => handleParChange(item.id, Number(e.target.value))}
                          className="w-16 text-center px-2 py-1.5 border border-navy-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-navy-500"
                          min="0"
                        />
                      </td>
                      <td className="px-5 py-3.5 text-center">
                        {isLow ? (
                          <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium bg-red-100 text-red-700">
                            <i className="ri-alert-line" /> Refill Needed
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium bg-green-100 text-green-700">
                            <i className="ri-check-line" /> OK
                          </span>
                        )}
                      </td>
                      <td className="px-5 py-3.5 text-center">
                        <div className="flex items-center justify-center gap-1">
                          <button
                            onClick={() => {
                              setSelectedItem(item);
                              setTxMode('transfer');
                              setTxForm({ qty: '', vendor_id: '', dept: activeDept, reason: '', bill_no: '', price: '' });
                              setShowTxModal(true);
                            }}
                            className="px-3 py-1.5 text-xs font-medium text-blue-600 bg-blue-50 hover:bg-blue-100 rounded-lg transition-colors cursor-pointer whitespace-nowrap"
                          >
                            <i className="ri-arrow-left-right-line mr-1" />Refill
                          </button>
                          <button
                            onClick={() => {
                              if (current <= 0) { addToast('error', 'No stock to consume'); return; }
                              setSelectedItem(item);
                              setTxMode('stock-out');
                              setTxForm({ qty: '', vendor_id: '', dept: activeDept, reason: '', bill_no: '', price: '' });
                              setShowTxModal(true);
                            }}
                            className="px-3 py-1.5 text-xs font-medium text-amber-600 bg-amber-50 hover:bg-amber-100 rounded-lg transition-colors cursor-pointer whitespace-nowrap"
                          >
                            <i className="ri-arrow-up-circle-line mr-1" />Use
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
                {deptItems.length === 0 && (
                  <tr>
                    <td colSpan={5} className="px-5 py-8 text-center text-navy-400">
                      <i className="ri-inbox-line text-2xl block mb-2" />
                      No tracked items for {activeDept}. Set par levels on Main Store items to track department stock.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Low Stock Tab Content */}
      {activeTab === 'low-stock' && (
        <div className="space-y-4">
          {/* Main Store Low Stock */}
          <div className="bg-white rounded-xl border border-red-100 overflow-hidden">
            <div className="p-4 border-b border-red-100 bg-red-50">
              <h3 className="font-semibold text-red-800">Main Store — Low Stock ({lowStockItems.length} items)</h3>
              <p className="text-xs text-red-600 mt-0.5">Order these items from vendors to avoid running out</p>
            </div>
            {lowStockItems.length === 0 ? (
              <div className="p-6 text-center text-green-600"><i className="ri-check-double-line text-2xl" /><div className="text-sm mt-1">All items are sufficiently stocked!</div></div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3 p-4">
                {lowStockItems.map((item) => (
                  <div key={item.id} className="p-4 border border-red-100 rounded-lg bg-red-50/50">
                    <div className="flex items-start justify-between mb-2">
                      <div>
                        <div className="font-medium text-navy-800 text-sm">{item.name}</div>
                        <div className="text-xs text-navy-500">{item.category} · {item.unit}</div>
                      </div>
                      <div className="text-right">
                        <div className="text-lg font-bold text-red-600">{item.current_qty}</div>
                        <div className="text-xs text-navy-400">of {item.min_stock_level} min</div>
                      </div>
                    </div>
                    <div className="w-full bg-navy-100 rounded-full h-1.5 mb-3">
                      <div className="bg-red-500 h-1.5 rounded-full" style={{ width: `${Math.min(100, (item.current_qty / item.min_stock_level) * 100)}%` }} />
                    </div>
                    <button onClick={() => openTx(item, 'stock-in')} className="w-full px-3 py-2 text-xs font-medium text-white bg-red-600 hover:bg-red-700 rounded-lg transition-colors cursor-pointer whitespace-nowrap">
                      <i className="ri-shopping-cart-line mr-1" />Order from Vendor
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Ledger Tab Content */}
      {activeTab === 'ledger' && (
        <>
          <div className="bg-white rounded-xl border border-navy-100 p-4 mb-6">
            <label className="block text-xs font-medium text-navy-500 mb-1.5">Select Item</label>
            <select value={ledgerItemId} onChange={(e) => setLedgerItemId(e.target.value)} className="w-full max-w-md px-3 py-2.5 border border-navy-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-navy-500">
              <option value="">-- Choose an item --</option>
              {items.map((item) => (
                <option key={item.id} value={item.id}>{item.name} ({item.current_qty} {item.unit} in stock)</option>
              ))}
            </select>
          </div>

          {ledgerItemId && ledgerEntries.length > 0 && (
            <div className="bg-white rounded-xl border border-navy-100 overflow-hidden">
              <div className="p-4 border-b border-navy-100">
                <h3 className="font-semibold text-navy-800">{items.find((i) => i.id === ledgerItemId)?.name} — Stock Ledger</h3>
                <p className="text-xs text-navy-500 mt-0.5">Complete in/out transaction history with running balance</p>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-navy-50">
                    <tr>
                      <th className="text-left px-4 py-2.5 text-xs font-semibold text-navy-600">Date</th>
                      <th className="text-left px-4 py-2.5 text-xs font-semibold text-navy-600">Reference / Type</th>
                      <th className="text-left px-4 py-2.5 text-xs font-semibold text-navy-600">Details</th>
                      <th className="text-center px-4 py-2.5 text-xs font-semibold text-navy-600">In (+)</th>
                      <th className="text-center px-4 py-2.5 text-xs font-semibold text-navy-600">Out (-)</th>
                      <th className="text-center px-4 py-2.5 text-xs font-semibold text-navy-600">Balance</th>
                    </tr>
                  </thead>
                  <tbody>
                    {ledgerEntries.map((entry, idx) => (
                      <tr key={idx} className="border-t border-navy-50 hover:bg-navy-50/50">
                        <td className="px-4 py-2.5 text-xs text-navy-500 whitespace-nowrap">{entry.date}</td>
                        <td className="px-4 py-2.5">
                          <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
                            entry.type.includes('GRN') || entry.type.includes('Stock In') ? 'bg-green-100 text-green-700' :
                            entry.type.includes('Out') ? 'bg-amber-100 text-amber-700' :
                            'bg-navy-100 text-navy-600'
                          }`}>{entry.type}</span>
                          {entry.ref && <div className="text-xs text-navy-400 mt-0.5">{entry.ref}</div>}
                        </td>
                        <td className="px-4 py-2.5 text-xs text-navy-600 max-w-[250px]">{entry.details}</td>
                        <td className="px-4 py-2.5 text-center text-xs font-bold text-green-600">
                          {entry.qty > 0 ? `+${entry.qty}` : ''}
                        </td>
                        <td className="px-4 py-2.5 text-center text-xs font-bold text-red-600">
                          {entry.qty < 0 ? entry.qty : ''}
                        </td>
                        <td className={`px-4 py-2.5 text-center text-xs font-bold ${entry.balance < 0 ? 'text-red-600' : 'text-navy-800'}`}>
                          {entry.balance}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {ledgerItemId && ledgerEntries.length === 0 && (
            <div className="bg-white rounded-xl border border-navy-100 p-10 text-center text-navy-400">
              <i className="ri-book-open-line text-3xl block mb-3" />
              <p className="text-sm">No transactions for this item yet.</p>
            </div>
          )}

          {!ledgerItemId && (
            <div className="bg-white rounded-xl border border-navy-100 p-10 text-center text-navy-400">
              <i className="ri-book-open-line text-3xl block mb-3" />
              <p className="text-sm">Select an item to view its complete stock movement history.</p>
            </div>
          )}
        </>
      )}

      {/* Add Item Modal */}
      {showForm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-md mx-4 overflow-hidden">
            <div className="px-6 py-4 border-b border-navy-100 flex items-center justify-between">
              <h3 className="font-semibold text-navy-800">Add Inventory Item</h3>
              <button onClick={() => setShowForm(false)} className="text-navy-400 hover:text-navy-600 cursor-pointer"><i className="ri-close-line text-xl" /></button>
            </div>
            <div className="p-6 space-y-4">
              <div><label className="block text-sm font-medium text-navy-700 mb-1.5">Item Name *</label><input type="text" className="w-full px-3 py-2.5 border border-navy-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-navy-500" value={formData.name} onChange={(e) => setFormData((p) => ({ ...p, name: e.target.value }))} /></div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div><label className="block text-sm font-medium text-navy-700 mb-1.5">Category</label><select className="w-full px-3 py-2.5 border border-navy-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-navy-500" value={formData.category} onChange={(e) => setFormData((p) => ({ ...p, category: e.target.value }))}>{CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}</select></div>
                <div><label className="block text-sm font-medium text-navy-700 mb-1.5">Unit *</label><input type="text" placeholder="e.g. Pieces, Liters" className="w-full px-3 py-2.5 border border-navy-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-navy-500" value={formData.unit} onChange={(e) => setFormData((p) => ({ ...p, unit: e.target.value }))} /></div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div><label className="block text-sm font-medium text-navy-700 mb-1.5">Min Stock Level</label><input type="number" className="w-full px-3 py-2.5 border border-navy-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-navy-500" value={formData.min_stock_level} onChange={(e) => setFormData((p) => ({ ...p, min_stock_level: e.target.value }))} /></div>
                <div><label className="block text-sm font-medium text-navy-700 mb-1.5">Current Quantity</label><input type="number" className="w-full px-3 py-2.5 border border-navy-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-navy-500" value={formData.current_qty} onChange={(e) => setFormData((p) => ({ ...p, current_qty: e.target.value }))} /></div>
              </div>
            </div>
            <div className="px-6 py-4 border-t border-navy-100 flex gap-3">
              <button onClick={() => setShowForm(false)} className="flex-1 px-4 py-2.5 border border-navy-200 rounded-lg text-sm font-medium text-navy-600 hover:bg-navy-50 transition-colors cursor-pointer">Cancel</button>
              <button onClick={handleAddItem} className="flex-1 px-4 py-2.5 bg-navy-700 text-white rounded-lg text-sm font-medium hover:bg-navy-800 transition-colors cursor-pointer">Add Item</button>
            </div>
          </div>
        </div>
      )}

      {/* Transaction Modal */}
      {showTxModal && selectedItem && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-md mx-4 overflow-hidden">
            <div className="px-6 py-4 border-b border-navy-100 flex items-center justify-between">
              <h3 className="font-semibold text-navy-800 capitalize">
                {txMode === 'stock-in' && `Stock Entry — ${selectedItem.name}`}
                {txMode === 'stock-out' && `Stock Usage — ${selectedItem.name}`}
                {txMode === 'adjust' && `Adjust Stock — ${selectedItem.name}`}
                {txMode === 'transfer' && `Transfer to Department — ${selectedItem.name}`}
              </h3>
              <button onClick={() => setShowTxModal(false)} className="text-navy-400 hover:text-navy-600 cursor-pointer"><i className="ri-close-line text-xl" /></button>
            </div>
            <div className="p-6 space-y-4">
              <div className="p-3 bg-navy-50 rounded-lg text-sm text-navy-600">
                <span className="font-medium">Current Main Store:</span> {selectedItem.current_qty} {selectedItem.unit}
                {txForm.dept && (
                  <span className="ml-4">
                    <span className="font-medium">{txForm.dept} Stock:</span> {getDeptStock(txForm.dept, selectedItem.id)}
                  </span>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-navy-700 mb-1.5">
                  {txMode === 'stock-out' ? 'Quantity Used *' : txMode === 'adjust' ? 'Quantity (+/-) *' : 'Quantity *'}
                </label>
                <input type="number" className="w-full px-3 py-2.5 border border-navy-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-navy-500" value={txForm.qty} onChange={(e) => setTxForm((p) => ({ ...p, qty: e.target.value }))} min="1" />
              </div>

              {(txMode === 'stock-in') && (
                <>
                  <div>
                    <label className="block text-sm font-medium text-navy-700 mb-1.5">Vendor *</label>
                    <select className="w-full px-3 py-2.5 border border-navy-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-navy-500" value={txForm.vendor_id} onChange={(e) => setTxForm((p) => ({ ...p, vendor_id: e.target.value }))}>
                      <option value="">Select vendor</option>
                      {vendors.map((v) => <option key={v.id} value={v.id}>{v.name}</option>)}
                    </select>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div><label className="block text-sm font-medium text-navy-700 mb-1.5">Bill Number</label><input type="text" className="w-full px-3 py-2.5 border border-navy-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-navy-500" value={txForm.bill_no} onChange={(e) => setTxForm((p) => ({ ...p, bill_no: e.target.value }))} /></div>
                    <div><label className="block text-sm font-medium text-navy-700 mb-1.5">Total Price (Rs.)</label><input type="number" className="w-full px-3 py-2.5 border border-navy-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-navy-500" value={txForm.price} onChange={(e) => setTxForm((p) => ({ ...p, price: e.target.value }))} /></div>
                  </div>
                </>
              )}

              {(txMode === 'stock-out' || txMode === 'transfer') && (
                <div>
                  <label className="block text-sm font-medium text-navy-700 mb-1.5">Department *</label>
                  <select className="w-full px-3 py-2.5 border border-navy-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-navy-500" value={txForm.dept} onChange={(e) => setTxForm((p) => ({ ...p, dept: e.target.value }))}>
                    <option value="">Select department</option>
                    {DEPARTMENTS.map((d) => <option key={d} value={d}>{d}</option>)}
                  </select>
                </div>
              )}

              {(txMode === 'stock-out' || txMode === 'adjust') && (
                <div>
                  <label className="block text-sm font-medium text-navy-700 mb-1.5">{txMode === 'adjust' ? 'Reason *' : 'Notes'}</label>
                  <textarea rows={2} className="w-full px-3 py-2.5 border border-navy-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-navy-500 resize-none" value={txForm.reason} onChange={(e) => setTxForm((p) => ({ ...p, reason: e.target.value }))} maxLength={500} />
                </div>
              )}
            </div>
            <div className="px-6 py-4 border-t border-navy-100 flex gap-3">
              <button onClick={() => setShowTxModal(false)} className="flex-1 px-4 py-2.5 border border-navy-200 rounded-lg text-sm font-medium text-navy-600 hover:bg-navy-50 transition-colors cursor-pointer">Cancel</button>
              <button onClick={handleTxSave} className="flex-1 px-4 py-2.5 bg-navy-700 text-white rounded-lg text-sm font-medium hover:bg-navy-800 transition-colors cursor-pointer">
                {txMode === 'stock-in' ? 'Add to Stock' : txMode === 'stock-out' ? 'Record Usage' : txMode === 'transfer' ? 'Transfer Stock' : 'Save Adjustment'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}